import { Injectable } from '@angular/core';
import { DataStorage } from '../data/data-storage';
import { HttpClient } from '@angular/common/http';
import { getApiUrl } from '../helpers/rest-utils';
import { map, forkJoin, Observable, of, tap } from 'rxjs';
import { UserService } from '../user/user.service';
import { Business, BusinessInformation, BusinessPhoneNumber, BusinessPhoneNumberType, DisconnectedIntegration, Service, TextLink, TransferDepartment, TransferDepartmentType } from './business';
import { formatPhoneNumber } from '../helpers/utils';


@Injectable({
  providedIn: 'root'
})
export class BusinessService {
  persistentStorage: DataStorage = DataStorage.persistent();
  businessTestNumbers: BusinessPhoneNumber[] = [];

  constructor(private http: HttpClient, private user: UserService) {
  }

  get businessId(): string {
    return this.user.activeBusiness?.id;
  }

  get businessInformationId(): string {
    return this.user.activeBusiness?.information?.id;
  }

  retrieveTestNumbers(): Observable<BusinessPhoneNumber[]> {
    if (!this.businessId) {
      return of([]);
    }
    return this.http.get(`${getApiUrl()}businesses/${this.businessId}/phone-numbers?type=test`, {
      headers: { 'X-Business-ID': this.user.activeBusiness?.id }
    }).pipe(map((res: any) => res.data || []), tap((numbers) => this.businessTestNumbers = numbers));
  }

  updateInformation(businessInformationData: BusinessInformation) {
    return this.http.patch(`${getApiUrl()}business-information/${this.businessInformationId}`, businessInformationData, { headers: { 'X-Business-ID': this.user.activeBusiness?.id } })
      .pipe(map((res: any) => ({ data: res.data, sanitizationResult: res.sanitizationResult })));
  }

  update(business: Business) {
    return this.http.put(`${getApiUrl()}businesses/${this.businessId}`, business, { headers: { 'X-Business-ID': this.user.activeBusiness?.id } })
      .pipe(map((res: any) => res.data));
  }

  get isAppBannerDismissed(): boolean {
    return !!this.user.activeBusiness?.meta?.appBannerDismissed;
  }

  get isSidebarBannerDismissed(): boolean {
    return !!this.user.activeBusiness?.meta?.appBannerDismissedSidebar;
  }

  get isConversationsBannerDismissed(): boolean {
    return !!this.user.activeBusiness?.meta?.appBannerDismissedConversations;
  }

  dismissAppBanner(): Observable<any> {
    const business = this.user.activeBusiness;
    if (!business) { return of(null); }
    business.meta = { ...business.meta, appBannerDismissed: true };
    return this.update({ title: business.title, meta: { appBannerDismissed: true } });
  }

  dismissSidebarBanner(): Observable<any> {
    const business = this.user.activeBusiness;
    if (!business) { return of(null); }
    business.meta = { ...business.meta, appBannerDismissedSidebar: true };
    return this.update({ title: business.title, meta: { appBannerDismissedSidebar: true } });
  }

  get isWebsiteChatSelected(): boolean {
    return !!this.user.activeBusiness?.meta?.websiteChatSelected;
  }

  markWebsiteChatSelected(): Observable<any> {
    const business = this.user.activeBusiness;
    if (!business) { return of(null); }
    business.meta = { ...business.meta, websiteChatSelected: true };
    return this.update({ title: business.title, meta: { websiteChatSelected: true } });
  }

  get isWebsiteChatBannerDismissed(): boolean {
    return !!this.user.activeBusiness?.meta?.websiteChatBannerDismissedSidebar;
  }

  dismissWebsiteChatBanner(): Observable<any> {
    const business = this.user.activeBusiness;
    if (!business) { return of(null); }
    business.meta = { ...business.meta, websiteChatBannerDismissedSidebar: true };
    return this.update({ title: business.title, meta: { websiteChatBannerDismissedSidebar: true } });
  }

  get isLeadSourcesBannerDismissed(): boolean {
    return !!this.user.activeBusiness?.meta?.leadSourcesBannerDismissedSidebar;
  }

  dismissLeadSourcesBanner(): Observable<any> {
    const business = this.user.activeBusiness;
    if (!business) { return of(null); }
    business.meta = { ...business.meta, leadSourcesBannerDismissedSidebar: true };
    return this.update({ title: business.title, meta: { leadSourcesBannerDismissedSidebar: true } });
  }

  get isCrmIntegrationsBannerDismissed(): boolean {
    return !!this.user.activeBusiness?.meta?.crmIntegrationsBannerDismissedSidebar;
  }

  dismissCrmIntegrationsBanner(): Observable<any> {
    const business = this.user.activeBusiness;
    if (!business) { return of(null); }
    business.meta = { ...business.meta, crmIntegrationsBannerDismissedSidebar: true };
    return this.update({ title: business.title, meta: { crmIntegrationsBannerDismissedSidebar: true } });
  }

  get isWebsiteTextingBannerDismissed(): boolean {
    return !!this.user.activeBusiness?.meta?.websiteTextingBannerDismissedSidebar;
  }

  dismissWebsiteTextingBanner(): Observable<any> {
    const business = this.user.activeBusiness;
    if (!business) { return of(null); }
    business.meta = { ...business.meta, websiteTextingBannerDismissedSidebar: true };
    return this.update({ title: business.title, meta: { websiteTextingBannerDismissedSidebar: true } });
  }

  get isMessageTakingBannerDismissed(): boolean {
    return !!this.user.activeBusiness?.meta?.messageTakingBannerDismissedSidebar;
  }

  dismissMessageTakingBanner(): Observable<any> {
    const business = this.user.activeBusiness;
    if (!business) { return of(null); }
    business.meta = { ...business.meta, messageTakingBannerDismissedSidebar: true };
    return this.update({ title: business.title, meta: { messageTakingBannerDismissedSidebar: true } });
  }

  get isWaterfallTransfersBannerDismissed(): boolean {
    return !!this.user.activeBusiness?.meta?.waterfallTransfersBannerDismissedSidebar;
  }

  dismissWaterfallTransfersBanner(): Observable<any> {
    const business = this.user.activeBusiness;
    if (!business) { return of(null); }
    business.meta = { ...business.meta, waterfallTransfersBannerDismissedSidebar: true };
    return this.update({ title: business.title, meta: { waterfallTransfersBannerDismissedSidebar: true } });
  }

  dismissConversationsBanner(): Observable<any> {
    const business = this.user.activeBusiness;
    if (!business) { return of(null); }
    business.meta = { ...business.meta, appBannerDismissedConversations: true };
    return this.update({ title: business.title, meta: { appBannerDismissedConversations: true } });
  }

  get hasHubspotContactLimitReached(): boolean {
    return this.user.activeBusiness?.crmContactLimit?.provider === 'hubspot';
  }

  get hubspotContactLimitToastDismissedAt(): string | undefined {
    return this.user.activeBusiness?.meta?.hubspotContactLimitToastDismissedAt;
  }

  dismissHubspotContactLimitToast(): Observable<any> {
    const business = this.user.activeBusiness;
    if (!business) { return of(null); }
    const hubspotContactLimitToastDismissedAt = new Date().toISOString();
    business.meta = { ...business.meta, hubspotContactLimitToastDismissedAt };
    return this.update({ title: business.title, meta: { hubspotContactLimitToastDismissedAt } });
  }

  get disconnectedIntegrations(): DisconnectedIntegration[] {
    return this.user.activeBusiness?.disconnectedIntegrations || [];
  }

  get disconnectedIntegrationToastDismissedAt(): string | undefined {
    return this.user.activeBusiness?.meta?.disconnectedIntegrationToastDismissedAt;
  }

  dismissDisconnectedIntegrationToast(): Observable<any> {
    const business = this.user.activeBusiness;
    if (!business) { return of(null); }
    const disconnectedIntegrationToastDismissedAt = new Date().toISOString();
    business.meta = { ...business.meta, disconnectedIntegrationToastDismissedAt };
    return this.update({ title: business.title, meta: { disconnectedIntegrationToastDismissedAt } });
  }

  updateServices(services: Service[]) {
    return this.http.post(`${getApiUrl()}businesses/${this.businessId}/services/batch`, { services }, { headers: { 'X-Business-ID': this.user.activeBusiness?.id } })
      .pipe(map((res: any) => res.data));
  }

  updateTransferDepartments(departments: TransferDepartment[]) {
    for (const department of departments) {
      // Preserve waterfall type if explicitly set. Otherwise derive from isWarmTransfer flag.
      if (department.type !== TransferDepartmentType.Waterfall) {
        department.type = department.isWarmTransfer ? TransferDepartmentType.Live : TransferDepartmentType.Simple;
      }
      for (const destination of (department.destinations || [])) {
        destination.number = formatPhoneNumber(destination.number);
      }
    }
    return this.http.post(`${getApiUrl()}businesses/${this.businessId}/transfer-departments/batch`, { departments }, { headers: { 'X-Business-ID': this.user.activeBusiness?.id } })
      .pipe(map((res: any) => res.data));
  }

  updateTextLinks(messageTemplates: TextLink[]) {
    return this.http.post(`${getApiUrl()}message-templates/batch`, { messageTemplates }, { headers: { 'X-Business-ID': this.user.activeBusiness?.id } })
      .pipe(map((res: any) => res.data));
  }

  updatePhoneNumber(phoneNumber: BusinessPhoneNumber): Observable<any> {
    if (phoneNumber.id && !phoneNumber.number) {
      return this.deletePhoneNumber(phoneNumber);
    } else if (phoneNumber.id && phoneNumber.number) {
      return this.updateExistingPhoneNumber(phoneNumber);
    } else {
      return this.createPhoneNumber(phoneNumber);
    }
  }

  private deletePhoneNumber(phoneNumber: BusinessPhoneNumber): Observable<any> {

    return this.http.delete(`${getApiUrl()}businesses/${this.businessId}/phone-numbers/${phoneNumber.id}`, {
      headers: { 'X-Business-ID': this.user.activeBusiness?.id }
    }).pipe(map((res: any) => res.data));
  }

  private updateExistingPhoneNumber(phoneNumber: BusinessPhoneNumber): Observable<any> {
    if (phoneNumber.id && phoneNumber.number) {
      phoneNumber.number = formatPhoneNumber(phoneNumber.number);
      return this.http.put(`${getApiUrl()}businesses/${this.businessId}/phone-numbers/${phoneNumber.id}`, phoneNumber, {
        headers: { 'X-Business-ID': this.user.activeBusiness?.id }
      }).pipe(map((res: any) => res.data));
    }
    return of(null);
  }

  private createPhoneNumber(phoneNumber: BusinessPhoneNumber): Observable<any> {
    if (!phoneNumber.id && phoneNumber.number) {
      phoneNumber.number = formatPhoneNumber(phoneNumber.number);
      return this.http.post(`${getApiUrl()}businesses/${this.businessId}/phone-numbers`, phoneNumber, {
        headers: { 'X-Business-ID': this.user.activeBusiness?.id }
      }).pipe(map((res: any) => res.data));
    }
    return of(null);
  }
}
